import { Observable, BehaviorSubject, of as observableOf ,of } from 'rxjs';
import { mergeMap, catchError, share } from "rxjs/operators";
import { AuthenticationService } from "../AuthenticationService";
import { addProvider, IProviderDescriptor } from '../ServiceUtil';
import { InsuranceProviderDelegate } from './insuranceProvider.delegate';
import {
    CreateInsuranceProviderRequest,
    InsuranceDetailData,
    InsurancePlan,
    InsuranceProvider,
    InsuranceProviderResponse,
    InsurancePlanResponse,
    InsurancePlanDetailResponse,
    INewInsurancePlanData
} from './insuranceProvider.interface';
export class InsuranceProviderService {
   
    private insuranceData: InsuranceProvider[] = null;
    private insuranceSubject: BehaviorSubject<InsuranceProvider[]> = new BehaviorSubject(null);
    public insuranceProvider: Observable<InsuranceProvider[]> = this.insuranceSubject;
 
    private insuranceFilteredSubject: BehaviorSubject<InsuranceProvider[]> = new BehaviorSubject(null);
    public insuranceFiltered: Observable<InsuranceProvider[]> = this.insuranceFilteredSubject;
 
    private insurancePlanSubject: BehaviorSubject<InsurancePlan[]> = new BehaviorSubject(null);
    public insurancePlans: Observable<InsurancePlan[]> = this.insurancePlanSubject;
 
    private selectedProviderData: InsuranceProvider = null;
    private selectedProviderSubject: BehaviorSubject<InsuranceProvider> = new BehaviorSubject(null);
    public selectedProvider: Observable<InsuranceProvider> = this.selectedProviderSubject;
 
    private selectedProviderId: number = 0;
    public filterEmployeStatus: boolean = true;
 
    constructor(private insuranceDelegate: InsuranceProviderDelegate, private authenticationService: AuthenticationService)
    {
    }
 
    public get insuranceProviderId(): number
    {
        return this.selectedProviderId;
    }
 
    public setSelectedProviderId(insuranceProviderId: number): void
    {
        this.selectedProviderId = insuranceProviderId;
    }
 
    public getProviderByStatus(active: boolean): void
    {
        this.filterEmployeStatus = active;
        const data = this.insuranceData ? this.insuranceData.filter(x => x.active === active) : [];
        this.insuranceFilteredSubject.next(data);
    }
 
    public loadPlansForSelectedProvider(insuranceProviderId: number): void
    {
        const provider = this.selectedProviderData?.insuranceProviderId === insuranceProviderId
            ? this.selectedProviderData
            : (this.insuranceData || []).find(p => p.insuranceProviderId === insuranceProviderId);
        this.insurancePlanSubject.next(provider?.stInsurancePlans || []);
    }
 
    public getAllInsuranceProviders(hardReload: boolean = false): Observable<boolean>
    {
        if (this.insuranceData !== null && this.insuranceData.length > 0 && hardReload === false)
        {
            return observableOf(true);
        }
        const orgId = this.authenticationService.GetOrgId();
        const response = this.insuranceDelegate.getAllProviders(orgId);
        return this.processInsuranceProviders(response);
    }
 
    public getSelectedInsProviderById(insuranceId: number): Observable<boolean>
    {
        if (insuranceId === 0)
        {
            this.selectedProviderData = null;
            this.selectedProviderSubject.next(this.selectedProviderData);
            return observableOf(false);
        }
        var response = this.insuranceDelegate.getProviderById(insuranceId);
        return this.processSelectedProvider(response);
    }
 
    public createNewInsuranceProvider(insuranceProvider: CreateInsuranceProviderRequest): Observable<boolean>
    {
        const response = this.insuranceDelegate.createNewInsuranceProvider({
            ...insuranceProvider,
            ...this.authenticationService.getBaseRequest
        });
        return this.processNewProvider(response);
    }
 
    public createNewInsurancePlan(insurancePlan: INewInsurancePlanData): Observable<boolean>
    {
        const response = this.insuranceDelegate.createNewInsurancePlan({
            ...insurancePlan,
            ...this.authenticationService.getBaseRequest
        });
        return this.processNewPlan(response);
    }
 
    public updateInsurancePlan(insurancePlan: INewInsurancePlanData): Observable<boolean>
    {
        const response = this.insuranceDelegate.updateInsurancePlan({
            ...insurancePlan,
            ...this.authenticationService.getBaseRequest
        });
        return this.processUpdatePlan(response);
    }
 
    public createInsuranceDetail(insurancePlanDetail: InsuranceDetailData): Observable<boolean>
    {
        const response = this.insuranceDelegate.createInsurancePlanDetails({
            ...insurancePlanDetail,
            ...this.authenticationService.getBaseRequest
        });
        return this.processNewDetail(response);
    }
 
    public updateInsurancePlanDetail(insurancePlanDetail: InsuranceDetailData): Observable<boolean>
    {
        const response = this.insuranceDelegate.updateInsurancePlanDetails({
            ...insurancePlanDetail,
            ...this.authenticationService.getBaseRequest
        });
        return this.processUpdateDetail(response);
    }
 
    // ─── Private process methods ───────────────────────────────────────────────
 
    private processInsuranceProviders(response: Observable<InsuranceProviderResponse>): Observable<boolean>
    {
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
 
        return response.pipe(mergeMap((response: InsuranceProviderResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
 
        function onSuccess(providerResponse: InsuranceProviderResponse)
        {
            if (providerResponse.success && providerResponse.insuranceProviders && providerResponse.insuranceProviders.length > 0)
            {
                this.selectedProviderId = providerResponse.insuranceProviderId ? providerResponse.insuranceProviderId : this.selectedProviderId;
                this.insuranceData = providerResponse.insuranceProviders;
                this.insuranceSubject.next(this.insuranceData);
                this.selectedProviderData = this.insuranceData.find(x => x.insuranceProviderId === this.selectedProviderId) || null;
                this.selectedProviderSubject.next(this.selectedProviderData);
                this.insuranceFilteredSubject.next(this.insuranceData);
                this.getProviderByStatus(this.filterEmployeStatus);
            }
        }
 
        function onFault(error: any)
        {
            // InsuranceProviderService.logger.error(`processInsuranceProviders Fault - Error (${JSON.stringify(error)})`);
        }
    }
 
    private processSelectedProvider(response: Observable<InsuranceProviderResponse>): Observable<boolean>
    {
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
 
        return response.pipe(mergeMap((response: InsuranceProviderResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
 
        function onSuccess(providerResponse: InsuranceProviderResponse)
        {
            if (providerResponse.success && providerResponse.insuranceProvider)
            {
                this.selectedProviderId = providerResponse.insuranceProviderId ? providerResponse.insuranceProviderId : this.selectedProviderId;
                this.selectedProviderData = providerResponse.insuranceProvider;
                this.selectedProviderSubject.next(this.selectedProviderData);
                this.loadPlansForSelectedProvider(this.selectedProviderId);
            }
        }
 
        function onFault(error: any)
        {
            // InsuranceProviderService.logger.error(`processSelectedProvider Fault - Error (${JSON.stringify(error)})`);
        }
    }
 
    private processNewProvider(response: Observable<InsuranceProviderResponse>): Observable<boolean> {
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
        return response.pipe(mergeMap((response: InsuranceProviderResponse) => observableOf(response.success)),
        catchError(() => observableOf(false)),
        share());
        function onSuccess(providerResponse: InsuranceProviderResponse) {
        if (providerResponse.success && providerResponse.insuranceProviders && providerResponse.insuranceProviders.length > 0) {
            const newProvider = providerResponse.insuranceProviders.find(p => p.insuranceProviderId === providerResponse.insuranceProviderId);
        if (!newProvider) return;
            const updatedData = [...(this.insuranceData || []), newProvider].map(p =>
        {
        if (p.insuranceProviderId !== newProvider.insuranceProviderId && p.year < newProvider.year && p.active){
            return { ...p, active: false };
        }
        return p;
        });
            this.insuranceData = updatedData;
            this.selectedProviderId = providerResponse.insuranceProviderId || this.selectedProviderId;
            this.selectedProviderData = newProvider;
            this.insuranceSubject.next(this.insuranceData);
            this.selectedProviderSubject.next(this.selectedProviderData);
            this.getProviderByStatus(this.filterEmployeStatus);
        }
    }
 
    function onFault(error: any)
    {
    // InsuranceProviderService.logger.error(`processNewProvider Fault - Error (${JSON.stringify(error)})`);
    }
    }
 
    private processNewPlan(response: Observable<InsurancePlanResponse>): Observable<boolean>
    {
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
 
        return response.pipe(mergeMap((response: InsurancePlanResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
 
        function onSuccess(planResponse: InsurancePlanResponse)
        {
            if (planResponse.success && planResponse.insurancePlans && planResponse.insurancePlans.length > 0 && this.selectedProviderData)
            {
                const newPlan = planResponse.insurancePlans.find(p => p.insurancePlanId === planResponse.insurancePlanId);
                if (newPlan)
                {
                    this.selectedProviderData.stInsurancePlans = [
                        ...(this.selectedProviderData.stInsurancePlans || []),
                        newPlan
                    ];
                    this.emitSelectedProvider();
                }
            }
        }
 
        function onFault(error: any)
        {
            // InsuranceProviderService.logger.error(`processNewPlan Fault - Error (${JSON.stringify(error)})`);
        }
    }
 
    private processUpdatePlan(response: Observable<InsurancePlanResponse>): Observable<boolean>
    {
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
 
        return response.pipe(mergeMap((response: InsurancePlanResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
        function onSuccess(planResponse: InsurancePlanResponse){
            if (planResponse.success && planResponse.insurancePlans && planResponse.insurancePlans.length > 0 && this.selectedProviderData){
                const plans = [...(this.selectedProviderData.stInsurancePlans || [])];
                planResponse.insurancePlans.forEach(updatedPlan =>{
                const idx = plans.findIndex(p => p.insurancePlanId === updatedPlan.insurancePlanId);
            if (idx > -1){
                const preservedDetails = (updatedPlan.insuranceDetailData && updatedPlan.insuranceDetailData.length > 0)
                ? updatedPlan.insuranceDetailData: plans[idx].insuranceDetailData;plans[idx] = { ...plans[idx], ...updatedPlan, insuranceDetailData: preservedDetails };
            }
        });
            this.selectedProviderData.stInsurancePlans = plans;
            this.emitSelectedProvider();
            }
        }
 
        function onFault(error: any)
        {
            // InsuranceProviderService.logger.error(`processUpdatePlan Fault - Error (${JSON.stringify(error)})`);
        }
    }
 
    private processNewDetail(response: Observable<InsurancePlanDetailResponse>): Observable<boolean>
    {
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
 
        return response.pipe(mergeMap((response: InsurancePlanDetailResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
 
        function onSuccess(detailResponse: InsurancePlanDetailResponse)
        {
            if (detailResponse.success && detailResponse.insuranceDetailData && detailResponse.insuranceDetailData.length > 0 && this.selectedProviderData)
            {
                const newDetail = detailResponse.insuranceDetailData.find(d => d.insuranceDetailId === detailResponse.insuranceDetailId);
                if (!newDetail) return;
                const plan = (this.selectedProviderData.stInsurancePlans || [])
                    .find(p => p.insurancePlanId === newDetail.insurancePlanId);
                if (plan)
                {
                    plan.insuranceDetailData = [...(plan.insuranceDetailData || []), newDetail];
                    this.emitSelectedProvider();
                }
            }
        }
 
        function onFault(error: any)
        {
            // InsuranceProviderService.logger.error(`processNewDetail Fault - Error (${JSON.stringify(error)})`);
        }
    }
 
    private processUpdateDetail(response: Observable<InsurancePlanDetailResponse>): Observable<boolean>
    {
        response.subscribe(onSuccess.bind(this), onFault.bind(this));
 
        return response.pipe(mergeMap((response: InsurancePlanDetailResponse) => observableOf(response.success)),
            catchError(() => observableOf(false)),
            share());
 
        function onSuccess(detailResponse: InsurancePlanDetailResponse)
        {
            if (detailResponse.success && detailResponse.insuranceDetailData && detailResponse.insuranceDetailData.length > 0 && this.selectedProviderData)
            {
                detailResponse.insuranceDetailData.forEach(updatedDetail =>
                {
                    const plan = (this.selectedProviderData.stInsurancePlans || [])
                        .find(p => p.insurancePlanId === updatedDetail.insurancePlanId);
                    if (plan)
                    {
                        const details = [...(plan.insuranceDetailData || [])];
                        const idx = details.findIndex(d => d.insuranceDetailId === updatedDetail.insuranceDetailId);
                        if (idx > -1)
                        {
                            details[idx] = updatedDetail;
                        }
                        plan.insuranceDetailData = details;
                    }
                });
                this.emitSelectedProvider();
            }
        }
 
        function onFault(error: any)
        {
            // InsuranceProviderService.logger.error(`processUpdateDetail Fault - Error (${JSON.stringify(error)})`);
        }
    }
 
    public emitSelectedProvider(): void
    {
        this.selectedProviderData = { ...this.selectedProviderData };
        this.selectedProviderSubject.next(this.selectedProviderData);
        this.loadPlansForSelectedProvider(this.selectedProviderId);
    }
}
 